import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getCompanyDepositsProvisional } from '~/apis/companyDeposits/provisional';
import {
  CompanyDepositProvisionalType,
  CompanyDepositType,
} from '~/types/resources/CompanyDepositType.ts';
import { getCompanyDeposits } from '../../../apis/companyDeposits/index';
import { getRedeemableStoreCouponHistories } from '../../../apis/redeemableStoreCouponHistories/index';
import { useAuth } from '../../../components/auth/AuthProvider.tsx';
import { ContentsHeader } from '../../../components/common/ContentsHeader';
import { CompanyDepositCardList } from '../../../components/companyDeposit/CardList';
import { RedeemableStoreCouponHistoryMobileCardList } from '../../../components/redeemableStoreCouponHistory/MobileCardList/index';
import { RedeemableStoreCouponHistoryTableContent } from '../../../components/redeemableStoreCouponHistory/TableContent/index';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { RedeemableStoreCouponHistoryTableData } from '../../../types/redeemableStoreCouponHistory/RedeemableStoreCouponHistoryTableData';
import {
  ActionTableSortParamsType,
  defaultActionTableSortParams,
} from '../../../types/table/ActionTableSortParamsType';
import { convertDateTime } from '../../../utils/dateTime';
import { handleSorting } from '../../../utils/sortings';
import styles from './styles.module.scss';

export const IndexPaymentReport: React.FC = () => {
  const isMobile = useIsMobile();
  const [filteredCompanyDeposits, setFilteredCompanyDeposits] = useState<
    (CompanyDepositProvisionalType | CompanyDepositType)[]
  >([]);
  const [filteredRedeemableStoreCouponHistories, setFilteredRedeemableStoreCouponHistories] =
    useState<RedeemableStoreCouponHistoryTableData[]>([]);
  const [isCompanyDepositsLoading, setCompanyDepositsIsLoading] = useState<boolean>(true);
  const [isRedeemableStoreCouponHistoriesLoading, setRedeemableStoreCouponHistoriesIsLoading] =
    useState<boolean>(true);
  const { signOut } = useAuth();
  const [sortParams, setSortParams] = useState<ActionTableSortParamsType>({
    ...defaultActionTableSortParams,
    column: 'no',
  });

  const { showToast } = useToast();

  const getCompanyDepositsData = async () => {
    try {
      const { data: prevDepositsData } = await getCompanyDeposits(signOut, undefined);
      const { data: provisionalData } = await getCompanyDepositsProvisional(signOut);

      setFilteredCompanyDeposits([
        provisionalData.company_deposit_provisional,
        ...(prevDepositsData.company_deposits.length > 0
          ? [prevDepositsData.company_deposits[0]]
          : []),
      ]);
    } catch {
      showToast({ message: 'データの取得に失敗しました。', status: 'danger' });
    } finally {
      setCompanyDepositsIsLoading(false);
    }
  };

  const getRedeemableStoreCouponHistoriesData = async () => {
    try {
      const { data } = await getRedeemableStoreCouponHistories(signOut, undefined);

      const processedUsers = data.redeemable_store_coupons.map(redeemable_store_coupon => {
        return {
          id: redeemable_store_coupon.id,
          amount: redeemable_store_coupon.amount,
          issue_identity: '', // APIからデータが返ってこないので値をセットしない
          branch: redeemable_store_coupon.store.branch,
          redeemed_at: convertDateTime(redeemable_store_coupon.redeemed_at),
          is_cancelled: redeemable_store_coupon.is_cancelled,
          three_letter_code: redeemable_store_coupon.three_letter_code,
        };
      });

      setFilteredRedeemableStoreCouponHistories(processedUsers.slice(0, 10));
    } catch {
      showToast({ message: 'データの取得に失敗しました。', status: 'danger' });
    } finally {
      setRedeemableStoreCouponHistoriesIsLoading(false);
    }
  };

  useEffect(() => {
    getCompanyDepositsData();
    getRedeemableStoreCouponHistoriesData();
  }, []);

  return (
    <div className={styles.Root} data-testid='clients-page'>
      <div className={styles.paymentWrapper}>
        <ContentsHeader title={isMobile ? 'サマリー' : 'ダッシュボードサマリー'} />
        <div className={styles.tableContainer}>
          {isCompanyDepositsLoading ? (
            <div className={styles.loading}>
              <CircularProgress size='1rem' color='inherit' />
            </div>
          ) : (
            <CompanyDepositCardList rows={filteredCompanyDeposits} />
          )}
        </div>
      </div>
      <div className={styles.transactionHistoryWrapper}>
        <ContentsHeader
          isSubContents={true}
          title={isMobile ? '最近の利用履歴 １０件' : '最近の地域ギフト利用10件'}
          actions={
            <Link
              to='/redeemable_store_coupon_histories'
              className={styles.link}
              target={'_blank'}
              rel='noopener noreferrer'
            >
              すべて見る
            </Link>
          }
        />
        <div className={styles.tableContainer}>
          {isRedeemableStoreCouponHistoriesLoading ? (
            <div className={styles.loading}>
              <CircularProgress size='1rem' color='inherit' />
            </div>
          ) : isMobile ? (
            <RedeemableStoreCouponHistoryMobileCardList
              rows={filteredRedeemableStoreCouponHistories}
              setIsLoading={setRedeemableStoreCouponHistoriesIsLoading}
            />
          ) : (
            <RedeemableStoreCouponHistoryTableContent
              rows={filteredRedeemableStoreCouponHistories}
              sortParams={sortParams}
              setIsLoading={setRedeemableStoreCouponHistoriesIsLoading}
              headerSort={column =>
                handleSorting(
                  column,
                  sortParams,
                  filteredRedeemableStoreCouponHistories,
                  setSortParams,
                  setFilteredRedeemableStoreCouponHistories
                )
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};
